/**
 * useMediaLibraryUrlState
 *
 * Syncs MediaLibrary navigation state with the URL hash so users can share links,
 * use the browser back/forward buttons, and deep-link to specific views.
 *
 * URL shape (all params namespaced with "zml-"):
 *   #zml-screen=<workspace|personal|bin|explore|home|selection|analytics|workspace-analytics>
 *   #zml-assetid=<uuid>           — presence implies the asset screen (unless zml-screen=analytics)
 *   #zml-type=<MediaType>         — filter, only meaningful on browser screens
 *   #zml-q=<search>               — search query, only meaningful on browser screens
 *
 * Examples:
 *   #zml-screen=workspace&zml-type=image&zml-q=tree
 *   #zml-assetid=abc-123
 *   #zml-screen=analytics&zml-assetid=abc-123
 *   #zml-screen=workspace-analytics
 *
 * The `Screen` identifier is the single source of truth for "where am I in
 * navigation". The React component's internal `(view, source)` state is derived
 * from `Screen` via `screenToViewSource`, and pushed back as `Screen` via
 * `viewSourceToScreen`. Two separate URL params for view and source are
 * deliberately gone — they used to be able to drift into invalid combinations.
 *
 * Transient views (gltf-missing-files) are excluded because they rely on
 * upload-queue state that cannot be meaningfully reconstructed from a URL.
 */

import { useEffect, useRef, useCallback } from 'react';
import type { MediaLibraryView, AssetSource } from '../types';
import { MediaType } from '../types';

const PREFIX = 'zml';

/**
 * The user-visible "where am I" identifier. One value covers all of:
 *   - which top-level screen the user is on
 *   - which library source (for browser screens)
 *   - whether this is asset-detail or analytics
 *
 * Maps 1:1 to the Django sidebar's `data-page-key` sub-items where applicable.
 */
export type Screen =
  | 'workspace' // browser on workspace-library
  | 'personal'  // browser on user-library
  | 'bin'       // browser on trash
  | 'explore'   // explore view + explore source
  | 'home'      // home view
  | 'asset'     // asset detail view
  | 'analytics' // asset analytics view
  | 'workspace-analytics' // workspace-wide analytics dashboard
  | 'selection'; // multi-select picker

const ALL_SCREENS: ReadonlySet<Screen> = new Set([
  'workspace', 'personal', 'bin', 'explore', 'home', 'asset', 'analytics', 'workspace-analytics', 'selection',
]);

// ─── Codec ───────────────────────────────────────────────────────────────────

export interface UrlNavState {
  screen: Screen;
  /** Required for `asset` and `analytics` screens; null otherwise. */
  assetId: string | null;
  mediaType: MediaType | null;
  searchQuery: string;
}

function key(name: string) {
  return `${PREFIX}-${name}`;
}

function encodeUrlState(state: Partial<UrlNavState>): string {
  const params = new URLSearchParams();
  if (state.screen === 'asset' && state.assetId) {
    // Presence of zml-assetid alone implies the asset screen — keep the URL tight.
    params.set(key('assetid'), state.assetId);
  } else if (state.screen === 'analytics' && state.assetId) {
    params.set(key('screen'), 'analytics');
    params.set(key('assetid'), state.assetId);
  } else if (state.screen) {
    params.set(key('screen'), state.screen);
  }
  if (state.mediaType) params.set(key('type'), state.mediaType);
  if (state.searchQuery) params.set(key('q'), state.searchQuery);
  return params.toString();
}

/**
 * Exported so the component can seed its initial state from the URL before this hook
 * runs, without keeping a second copy of the rules that would drift as screens are added.
 */
export function decodeUrlState(hash: string): Partial<UrlNavState> {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(raw);
  const state: Partial<UrlNavState> = {};

  const assetId = params.get(key('assetid'));
  const screenParam = params.get(key('screen'));

  if (assetId) {
    state.assetId = assetId;
    state.screen = screenParam === 'analytics' ? 'analytics' : 'asset';
  } else if (screenParam && ALL_SCREENS.has(screenParam as Screen)) {
    // `asset`/`analytics` without an asset id are degenerate — fall through to default below.
    if (screenParam !== 'asset' && screenParam !== 'analytics') {
      state.screen = screenParam as Screen;
    }
  }

  const type = params.get(key('type'));
  if (type && Object.values(MediaType).includes(type as MediaType)) {
    state.mediaType = type as MediaType;
  }

  const q = params.get(key('q'));
  if (q) state.searchQuery = q;

  return state;
}

// ─── Screen ↔ internal (view, source) converters ─────────────────────────────

/**
 * Map a `Screen` to the internal React state representation.
 *
 * For screens that aren't tied to a library (asset/analytics/selection/home/explore),
 * `source` is set to a sensible default so the rest of the component sees a
 * well-formed state — but the value carries no user-visible meaning for those screens.
 */
export function screenToViewSource(screen: Screen): { view: MediaLibraryView; source: AssetSource } {
  switch (screen) {
    case 'workspace': return { view: 'browser', source: 'workspace-library' };
    case 'personal':  return { view: 'browser', source: 'user-library' };
    case 'bin':       return { view: 'browser', source: 'trash' };
    case 'explore':   return { view: 'explore', source: 'explore' };
    case 'home':      return { view: 'home',    source: 'user-library' };
    case 'asset':     return { view: 'asset',   source: 'workspace-library' };
    case 'analytics': return { view: 'analytics', source: 'workspace-library' };
    case 'workspace-analytics': return { view: 'workspace-analytics', source: 'workspace-library' };
    case 'selection': return { view: 'selection', source: 'user-library' };
  }
}

/**
 * Inverse of `screenToViewSource`: derive the canonical Screen from internal state.
 * The `view` is the primary signal; `source` only matters when view==='browser'.
 * Returns null for transient views (gltf-missing-files) that shouldn't appear in the URL.
 */
export function viewSourceToScreen(view: MediaLibraryView, source: AssetSource): Screen | null {
  switch (view) {
    case 'gltf-missing-files': return null;
    case 'home':      return 'home';
    case 'explore':   return 'explore';
    case 'asset':     return 'asset';
    case 'analytics': return 'analytics';
    case 'workspace-analytics': return 'workspace-analytics';
    case 'selection': return 'selection';
    case 'browser':
      if (source === 'workspace-library') return 'workspace';
      if (source === 'user-library')      return 'personal';
      if (source === 'trash')             return 'bin';
      // Browser on any other source (zappar-library, external provider id) has no
      // canonical screen of its own — fall back to workspace so the sidebar still
      // highlights something coherent.
      return 'workspace';
  }
}

// ─── Hash merging ────────────────────────────────────────────────────────────
//
// The hash may contain params unrelated to the media library. Preserve those
// by reading all existing hash params and only overwriting the zml-* ones.

function mergeIntoHash(state: Partial<UrlNavState>): string {
  const existing = new URLSearchParams(
    window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash
  );
  for (const k of [...existing.keys()]) {
    if (k.startsWith(`${PREFIX}-`)) existing.delete(k);
  }
  const encoded = new URLSearchParams(encodeUrlState(state));
  encoded.forEach((v, k) => existing.set(k, v));
  return existing.toString();
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseMediaLibraryUrlStateOptions {
  /** Called when the browser navigates back/forward to a stored MediaLibrary state. */
  onPopState: (state: Partial<UrlNavState>) => void;
}

export interface UseMediaLibraryUrlStateResult {
  /** Initial URL state read once on mount. Empty object if no zml-* params are present. */
  initialUrlState: Partial<UrlNavState>;
  /**
   * Call this whenever MediaLibrary navigation state changes. The URL hash is
   * updated, creating a browser-history entry for back/forward support.
   */
  pushUrlState: (state: Partial<UrlNavState>) => void;
}

export function useMediaLibraryUrlState(
  enabled: boolean,
  options: UseMediaLibraryUrlStateOptions
): UseMediaLibraryUrlStateResult {
  const { onPopState } = options;

  const initialUrlState = useRef<Partial<UrlNavState>>(
    enabled ? decodeUrlState(window.location.hash) : {}
  ).current;

  // Track the last hash we wrote so we can ignore the resulting hashchange event.
  const lastWrittenHash = useRef<string | null>(null);

  const pushUrlState = useCallback(
    (state: Partial<UrlNavState>) => {
      if (!enabled) return;
      const newHash = mergeIntoHash(state);
      const fullHash = `#${newHash}`;
      if (fullHash === window.location.hash) return;
      lastWrittenHash.current = fullHash;
      window.location.hash = newHash;
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    const handleHashChange = () => {
      const current = window.location.hash;
      if (lastWrittenHash.current === current) {
        lastWrittenHash.current = null;
        return;
      }
      const state = decodeUrlState(current);
      if (Object.keys(state).length > 0) {
        onPopState(state);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [enabled, onPopState]);

  return { initialUrlState, pushUrlState };
}
